import { Constants } from 'constants/constants'
import { TRANSLATION_KEYS } from 'locales/translation-keys'

export interface NotesPermissionCheck {
    canRead: boolean
    canWrite: boolean
    reason?: string
}

export const validateNotesPermissions = (
    userGroups: string[] | null,
    currentLocation: string,
    userStoreId: number | undefined,
    t: (key: string, options?: any) => string,
): NotesPermissionCheck => {
    const { permissions } = TRANSLATION_KEYS.notes

    if (!userGroups || userGroups.length === 0) {
        return {
            canRead: false,
            canWrite: false,
            reason: t(permissions.noNotesPermissions),
        }
    }

    const currentStoreId = parseInt(currentLocation.slice(1), 10) // Remove 'T' prefix, explicit radix

    if (Number.isNaN(currentStoreId)) {
        return {
            canRead: false,
            canWrite: false,
            reason: t(permissions.userLocationNotAvailable),
        }
    }

    const isSameLocation = userStoreId != null && currentStoreId === userStoreId

    const hasAdminAccess = userGroups.includes(
        Constants.GREENFIELD_GROUPS.ADMIN_PROD,
    )
    const hasReadAcrossLocations = userGroups.includes(
        Constants.GREENFIELD_GROUPS.NOTES_READ_ACROSS_LOCATIONS,
    )
    const hasReadSameLocation = userGroups.includes(
        Constants.GREENFIELD_GROUPS.NOTES_READ_SAME_LOCATION,
    )
    const hasWriteAccess = userGroups.includes(
        Constants.GREENFIELD_GROUPS.NOTES_WRITE,
    )

    if (hasAdminAccess) {
        return {
            canRead: true,
            canWrite: true,
        }
    }

    if (hasReadAcrossLocations) {
        return {
            canRead: true,
            canWrite: hasWriteAccess,
        }
    }

    if (hasReadSameLocation) {
        if (userStoreId == null) {
            return {
                canRead: false,
                canWrite: false,
                reason: t(permissions.userLocationNotAvailable),
            }
        }

        if (isSameLocation) {
            return {
                canRead: true,
                canWrite: hasWriteAccess,
            }
        } else {
            return {
                canRead: false,
                canWrite: false,
                reason: t(permissions.accessDeniedLocation, {
                    userStoreId,
                    currentStoreId,
                }),
            }
        }
    }

    if (hasWriteAccess) {
        if (userStoreId == null) {
            return {
                canRead: false,
                canWrite: false,
                reason: t(permissions.userLocationNotAvailable),
            }
        }

        if (isSameLocation) {
            return {
                canRead: true,
                canWrite: true,
            }
        } else {
            return {
                canRead: false,
                canWrite: false,
                reason: t(permissions.writeAccessLimited, { userStoreId }),
            }
        }
    }

    return {
        canRead: false,
        canWrite: false,
        reason: t(permissions.noNotesPermissions),
    }
}
